import { useState, useEffect } from 'react';
import { fetchAffiliateSummary, updateBookmaker } from '@/api';
import { LoadingPage } from '@/components/common/Loading';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link2, MousePointer, BarChart3, ExternalLink, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLink, setEditLink] = useState('');
  const [editCode, setEditCode] = useState('');

  const load = () => {
    setLoading(true);
    fetchAffiliateSummary()
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (bkmId) => {
    try {
      const updates = {};
      if (editLink) updates.affiliate_link = editLink;
      if (editCode) updates.affiliate_code = editCode;
      updates.tracking_enabled = true;
      await updateBookmaker(bkmId, updates);
      toast.success('Affiliate settings updated');
      setEditingId(null);
      load();
    } catch { toast.error('Update failed'); }
  };

  const toggleTracking = async (bkmId, enabled) => {
    try {
      await updateBookmaker(bkmId, { tracking_enabled: !enabled });
      toast.success(enabled ? 'Tracking disabled' : 'Tracking enabled');
      load();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <LoadingPage />;

  return (
    <div data-testid="affiliate-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
          <Link2 className="w-5 h-5 inline mr-2 text-emerald-400" />
          Affiliate Dashboard
        </h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard icon={MousePointer} label="Total Clicks" value={data?.total_clicks || 0} color="emerald" />
        <SummaryCard icon={Link2} label="Tracked" value={data?.tracked_bookmakers || 0} color="blue" />
        <SummaryCard icon={BarChart3} label="With Clicks" value={data?.bookmakers_with_clicks || 0} color="amber" />
        <SummaryCard icon={ExternalLink} label="Total Bookmakers" value={data?.total_bookmakers || 0} color="slate" />
      </div>

      {/* Top Performers */}
      {data?.top_performers?.some(b => b.click_count > 0) && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Top Performers</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_performers.filter(b => b.click_count > 0).slice(0, 5).map(b => (
              <div key={b.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3 text-center">
                <p className="text-emerald-400 font-bold text-lg tabular-nums">{b.click_count}</p>
                <p className="text-xs text-slate-400 truncate">{b.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Bookmakers Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/80">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Bookmaker</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Clicks</th>
              <th className="text-left px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Affiliate Link</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Tracking</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Last Click</th>
              <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.all_bookmakers?.map(bkm => (
              <tr key={bkm.id} className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`affiliate-row-${bkm.id}`}>
                <td className="px-4 py-3 text-slate-200 font-medium">{bkm.name}</td>
                <td className="px-3 py-3 text-center">
                  <span className={`tabular-nums font-bold ${bkm.click_count > 0 ? 'text-emerald-400' : 'text-slate-600'}`}>
                    {bkm.click_count}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {editingId === bkm.id ? (
                    <div className="flex gap-1.5">
                      <Input value={editLink} onChange={e => setEditLink(e.target.value)}
                        placeholder="https://affiliate.link/..."
                        className="bg-slate-800 border-slate-700 text-xs h-7 flex-1" />
                      <Input value={editCode} onChange={e => setEditCode(e.target.value)}
                        placeholder="Code"
                        className="bg-slate-800 border-slate-700 text-xs h-7 w-20" />
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500 truncate block max-w-[200px]">
                      {bkm.affiliate_link || 'Not set'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-center">
                  <button onClick={() => toggleTracking(bkm.id, bkm.tracking_enabled)}
                    data-testid={`toggle-tracking-${bkm.id}`}>
                    <Badge className={bkm.tracking_enabled
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 cursor-pointer text-xs'
                      : 'bg-slate-500/10 text-slate-500 border-slate-500/20 cursor-pointer text-xs'}>
                      {bkm.tracking_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </button>
                </td>
                <td className="px-3 py-3 text-center text-xs text-slate-500">
                  {bkm.last_click_at ? new Date(bkm.last_click_at).toLocaleDateString() : '-'}
                </td>
                <td className="px-3 py-3 text-center">
                  {editingId === bkm.id ? (
                    <div className="flex gap-1 justify-center">
                      <Button size="sm" onClick={() => handleSave(bkm.id)}
                        className="bg-emerald-500/10 text-emerald-400 h-7 w-7 p-0">
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" onClick={() => setEditingId(null)}
                        className="bg-slate-700 text-slate-400 h-7 w-7 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(bkm.id); setEditLink(bkm.affiliate_link || ''); setEditCode(bkm.affiliate_code || ''); }}
                      className="text-slate-500 hover:text-slate-300 h-7 text-xs"
                      data-testid={`edit-affiliate-${bkm.id}`}>
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    slate: 'text-slate-300 bg-slate-700/50 border-slate-600',
  };
  const c = colors[color] || colors.slate;
  return (
    <div className={`rounded-xl border p-4 ${c}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="w-5 h-5 mb-2 opacity-60" />
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs opacity-60 mt-0.5">{label}</p>
    </div>
  );
}
